import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import useUrlState, { param } from "../hooks/useUrlState";
import { apiErrorMessage } from "../services/api";
import { formatDate } from "../services/datetime";
import { fetchMembers } from "../services/loyaltyService";

const FILTERS = {
  search: param.string(),
  sort: param.oneOf(["recent", "points", "name"], "recent"),
  page: param.int(1),
};

/**
 * Loyalty → Members (`loyalty.view`): every client holding a card, with their
 * balance, stamps, tier and last activity. A row opens the member's card and
 * statement.
 */
export default function LoyaltyMembers() {
  const { t, i18n } = useTranslation();
  const { values, set } = useUrlState(FILTERS);
  const [search, setSearch] = useState(values.search);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setError("");
    fetchMembers({ page: values.page, search: values.search, sort: values.sort })
      .then((data) => !cancelled && setResult(data))
      .catch((err) => !cancelled && setError(apiErrorMessage(err, t("loyalty.loadFailed"))));
    return () => {
      cancelled = true;
    };
  }, [values.page, values.search, values.sort, t]);

  const members = result?.members ?? null;
  const meta = result?.meta;

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-bold">{t("loyalty.members.title")}</h1>
        <p className="text-sm text-ink-2">{t("loyalty.members.subtitle")}</p>
      </div>

      <form
        className="card mb-4 grid gap-3 p-4 sm:grid-cols-3"
        onSubmit={(e) => {
          e.preventDefault();
          set({ search: search.trim(), page: 1 });
        }}
      >
        <div className="sm:col-span-2">
          <label className="field-label" htmlFor="lm-search">
            {t("loyalty.members.search")}
          </label>
          <div className="flex gap-2">
            <input
              id="lm-search"
              type="search"
              className="field-input flex-1"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("loyalty.members.searchPlaceholder")}
            />
            <button type="submit" className="btn-secondary">
              {t("loyalty.members.find")}
            </button>
          </div>
        </div>
        <div>
          <label className="field-label" htmlFor="lm-sort">
            {t("loyalty.members.sort")}
          </label>
          <select
            id="lm-sort"
            className="field-input"
            value={values.sort}
            onChange={(e) => set({ sort: e.target.value, page: 1 })}
          >
            {["recent", "points", "name"].map((sort) => (
              <option key={sort} value={sort}>
                {t(`loyalty.members.sorts.${sort}`)}
              </option>
            ))}
          </select>
        </div>
      </form>

      {error && (
        <div
          className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text"
          role="alert"
        >
          {error}
        </div>
      )}

      <div className="card overflow-hidden">
        {members === null ? (
          <p className="py-12 text-center text-sm text-ink-2">{t("common.loading")}</p>
        ) : members.length === 0 ? (
          <p className="py-12 text-center text-sm text-ink-2">
            {values.search ? t("common.noMatches") : t("loyalty.members.empty")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-start text-sm">
              <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-ink-2">
                <tr>
                  <th className="px-4 py-3 text-start">{t("loyalty.members.client")}</th>
                  <th className="px-4 py-3 text-end">{t("loyalty.members.points")}</th>
                  <th className="px-4 py-3 text-end">{t("loyalty.members.stamps")}</th>
                  <th className="px-4 py-3 text-start">{t("loyalty.members.tier")}</th>
                  <th className="px-4 py-3 text-start">{t("loyalty.members.lastActivity")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {members.map((member) => (
                  <tr key={member.account_id} className="hover:bg-raised">
                    <td className="px-4 py-3">
                      {member.client ? (
                        <Link
                          to={`/loyalty/members/${member.client.id}`}
                          className="font-medium text-accent-text hover:underline"
                        >
                          {member.client.name}
                        </Link>
                      ) : (
                        t("common.none")
                      )}
                      {member.client?.phone && (
                        <p className="text-xs text-ink-2" dir="ltr">
                          {member.client.phone}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-end font-semibold tabular-nums" dir="ltr">
                      {member.points_balance}
                    </td>
                    <td className="px-4 py-3 text-end tabular-nums" dir="ltr">
                      {member.stamps_balance}
                    </td>
                    <td className="px-4 py-3">{member.tier?.name ?? t("common.none")}</td>
                    <td className="px-4 py-3 text-ink-2">
                      {member.last_activity_at
                        ? formatDate(member.last_activity_at, i18n.language)
                        : t("common.none")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {meta && meta.last_page > 1 && (
          <div className="flex items-center justify-between border-t border-line px-4 py-3 text-sm">
            <button
              type="button"
              className="btn-secondary"
              disabled={meta.current_page <= 1}
              onClick={() => set({ page: meta.current_page - 1 })}
            >
              {t("common.previous")}
            </button>
            <span className="text-ink-2">
              {t("common.pageOf", { current: meta.current_page, last: meta.last_page })}
            </span>
            <button
              type="button"
              className="btn-secondary"
              disabled={meta.current_page >= meta.last_page}
              onClick={() => set({ page: meta.current_page + 1 })}
            >
              {t("common.next")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
